import { Component, ElementRef, OnInit, AfterViewInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';

const STORAGE_KEY = 'easycode-a11y';

interface A11ySettings {
  fontSizeLevel: number;
  dyslexia: boolean;
  fontWeight: boolean;
  lineHeight: boolean;
  letterSpacing: boolean;
  textSpacing: boolean;
  highlightTitles: boolean;
  monochrome: boolean;
  lowSaturation: boolean;
  highSaturation: boolean;
  highContrast: boolean;
  lightContrast: boolean;
  darkContrast: boolean;
  stopAnimations: boolean;
  bigCursor: boolean;
  highlightLinks: boolean;
  readingGuide: boolean;
  hideImages: boolean;
}

const DEFAULT: A11ySettings = {
  fontSizeLevel: 0,
  dyslexia: false,
  fontWeight: false,
  lineHeight: false,
  letterSpacing: false,
  textSpacing: false,
  highlightTitles: false,
  monochrome: false,
  lowSaturation: false,
  highSaturation: false,
  highContrast: false,
  lightContrast: false,
  darkContrast: false,
  stopAnimations: false,
  bigCursor: false,
  highlightLinks: false,
  readingGuide: false,
  hideImages: false,
};

const COLOR_MODES: (keyof A11ySettings)[] = [
  'monochrome', 'lowSaturation', 'highSaturation', 'highContrast', 'lightContrast', 'darkContrast',
];

// 1×1 transparent GIF — used as cursor instead of `cursor: none`
// because Chrome ignores `cursor: none` on native interactive elements regardless of !important.
const BLANK_CURSOR = 'url("data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7") 0 0, none';

@Component({
  selector: 'app-accessibility-widget',
  templateUrl: './accessibility-widget.component.html',
  styleUrls: ['./accessibility-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AccessibilityWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('stack') stackRef!: ElementRef<HTMLElement>;
  @ViewChild('panel') panelRef!: ElementRef<HTMLElement>;
  @ViewChild('trigger') triggerRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('closeBtn') closeBtnRef!: ElementRef<HTMLButtonElement>;

  isOpen = false;
  settings: A11ySettings = { ...DEFAULT };
  mouseY = 0;

  private _bigCursorEl: HTMLElement | null = null;
  private _bigCursorStyleEl: HTMLStyleElement | null = null;
  private _bigCursorMutObs: MutationObserver | null = null;
  private _bigCursorInterval: ReturnType<typeof setInterval> | null = null;
  private _iubendaMutObs: MutationObserver | null = null;

  private _onMouseMoveBound = this._onMouseMove.bind(this);
  private _bigCursorMoveBound = this._bigCursorMove.bind(this);
  private _bigCursorLeaveBound = this._bigCursorLeave.bind(this);

  get fontSizeLevelLabel(): string {
    const l = this.settings.fontSizeLevel;
    return l === 0 ? '0' : l > 0 ? `+${l}` : `${l}`;
  }

  get hasAnyActive(): boolean {
    return (
      this.settings.fontSizeLevel !== 0 ||
      this.settings.dyslexia ||
      this.settings.fontWeight ||
      this.settings.lineHeight ||
      this.settings.letterSpacing ||
      this.settings.textSpacing ||
      this.settings.highlightTitles ||
      this.settings.monochrome ||
      this.settings.lowSaturation ||
      this.settings.highSaturation ||
      this.settings.highContrast ||
      this.settings.lightContrast ||
      this.settings.darkContrast ||
      this.settings.stopAnimations ||
      this.settings.bigCursor ||
      this.settings.highlightLinks ||
      this.settings.readingGuide ||
      this.settings.hideImages
    );
  }

  ngOnInit(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) this.settings = { ...DEFAULT, ...JSON.parse(saved) };
    } catch {}
    this.applySettings();
    document.addEventListener('mousemove', this._onMouseMoveBound);
  }

  ngAfterViewInit(): void {
    this._watchIubendaButton();
    this._updatePanelFocusability();
  }

  ngOnDestroy(): void {
    this._applyBigCursor(false);
    document.removeEventListener('mousemove', this._onMouseMoveBound);
    this._iubendaMutObs?.disconnect();
    this._iubendaMutObs = null;
  }

  private _onMouseMove(e: MouseEvent): void {
    if (this.settings.readingGuide) this.mouseY = e.clientY;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this._setIubendaVisibility(!this.isOpen);
    this._updatePanelFocusability();
    if (this.isOpen) {
      setTimeout(() => this.closeBtnRef?.nativeElement?.focus());
    } else {
      setTimeout(() => this.triggerRef?.nativeElement?.focus());
    }
  }

  private _updatePanelFocusability(): void {
    const panel = this.panelRef?.nativeElement;
    if (!panel) return;
    panel.querySelectorAll('button').forEach(btn => {
      (btn as HTMLButtonElement).tabIndex = this.isOpen ? 0 : -1;
    });
  }

  private _setIubendaVisibility(visible: boolean): void {
    const stack = this.stackRef?.nativeElement;
    if (!stack) return;
    const btn = stack.querySelector<HTMLElement>(
      '#iubenda-cs-preferences-link, .iubenda-cs-preferences-link, [id^="iubenda-"][class*="preferences"]'
    );
    if (!btn) return;
    btn.style.setProperty('display', visible ? '' : 'none', 'important');
  }

  onPanelKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.toggle();
      return;
    }
    const panel = this.panelRef?.nativeElement;
    if (e.key !== 'Tab' || !panel) return;

    const focusables = Array.from(panel.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  toggleFeature(feature: keyof A11ySettings): void {
    if (COLOR_MODES.includes(feature)) {
      const wasActive = this.settings[feature];
      COLOR_MODES.forEach(m => (this.settings[m] = false as never));
      (this.settings[feature] as boolean) = !(wasActive as boolean);
    } else {
      (this.settings[feature] as boolean) = !(this.settings[feature] as boolean);
    }
    this.applySettings();
    this._save();
  }

  increaseFontSize(): void {
    if (this.settings.fontSizeLevel < 4) {
      this.settings.fontSizeLevel++;
      this.applySettings();
      this._save();
    }
  }

  decreaseFontSize(): void {
    if (this.settings.fontSizeLevel > -2) {
      this.settings.fontSizeLevel--;
      this.applySettings();
      this._save();
    }
  }

  reset(): void {
    this.settings = { ...DEFAULT };
    this.applySettings();
    this._save();
  }

  applySettings(): void {
    const html = document.documentElement;
    html.style.fontSize =
      this.settings.fontSizeLevel === 0 ? '' : `${16 + this.settings.fontSizeLevel * 2}px`;

    const classMap: Record<string, boolean> = {
      'a11y-dyslexia': this.settings.dyslexia,
      'a11y-font-weight': this.settings.fontWeight,
      'a11y-line-height': this.settings.lineHeight,
      'a11y-letter-spacing': this.settings.letterSpacing,
      'a11y-text-spacing': this.settings.textSpacing,
      'a11y-highlight-titles': this.settings.highlightTitles,
      'a11y-monochrome': this.settings.monochrome,
      'a11y-low-saturation': this.settings.lowSaturation,
      'a11y-high-saturation': this.settings.highSaturation,
      'a11y-high-contrast': this.settings.highContrast,
      'a11y-light-contrast': this.settings.lightContrast,
      'a11y-dark-contrast': this.settings.darkContrast,
      'a11y-stop-animations': this.settings.stopAnimations,
      'a11y-big-cursor': this.settings.bigCursor,
      'a11y-highlight-links': this.settings.highlightLinks,
      'a11y-hide-images': this.settings.hideImages,
    };
    for (const [cls, active] of Object.entries(classMap)) {
      html.classList.toggle(cls, active);
    }
    this._applyBigCursor(this.settings.bigCursor);
  }

  // Draw visible big cursor as fixed-position div overlay.
  // OS cursor hidden via three-layer strategy:
  //  1. styles.scss — static CSS with blank-cursor !important
  //  2. <style> tag — same rule injected last in <head>, wins source-order ties
  //  3. setProperty — inline !important on every DOM element, bypasses all cascade
  //     MutationObserver keeps new nodes patched
  private _applyBigCursor(enable: boolean): void {
    if (enable) {
      if (!this._bigCursorEl) {
        const el = document.createElement('div');
        Object.assign(el.style, {
          position: 'fixed', top: '0', left: '0',
          width: '48px', height: '48px',
          backgroundImage: 'url("/assets/a11y/a11y-big-cursor.svg")',
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
          pointerEvents: 'none', zIndex: '2147483647',
          opacity: '0', willChange: 'transform',
        });
        document.body.appendChild(el);
        this._bigCursorEl = el;
      }

      if (!this._bigCursorStyleEl) {
        const style = document.createElement('style');
        style.id = 'a11y-big-cursor-style';
        style.textContent = [
          'html.a11y-big-cursor *,',
          'html.a11y-big-cursor *:hover,',
          'html.a11y-big-cursor *:focus,',
          'html.a11y-big-cursor *:focus-visible,',
          'html.a11y-big-cursor *:active,',
          'html.a11y-big-cursor *:focus-within',
          `{ cursor: ${BLANK_CURSOR} !important; }`,
        ].join('\n');
        document.head.appendChild(style);
        this._bigCursorStyleEl = style;
      }

      this._patchAllDomCursors();

      if (!this._bigCursorMutObs) {
        this._bigCursorMutObs = new MutationObserver(mutations => {
          mutations.forEach(m => {
            if (m.type === 'attributes') {
              if (m.target instanceof Element) {
                const el = m.target as HTMLElement;
                if (
                  el.style?.getPropertyValue('cursor') !== BLANK_CURSOR ||
                  el.style?.getPropertyPriority('cursor') !== 'important'
                ) {
                  this._patchElementCursor(el);
                }
              }
            } else {
              m.addedNodes.forEach(node => {
                if (node instanceof Element) {
                  this._patchElementCursor(node as HTMLElement);
                  (node as Element).querySelectorAll('*').forEach(child => this._patchElementCursor(child as HTMLElement));
                }
              });
            }
          });
        });
        this._bigCursorMutObs.observe(document.documentElement, {
          childList: true, subtree: true, attributes: true, attributeFilter: ['style'],
        });
      }

      document.addEventListener('mousemove', this._bigCursorMoveBound, { passive: true });
      document.addEventListener('mouseleave', this._bigCursorLeaveBound);

      if (!this._bigCursorInterval) {
        this._bigCursorInterval = setInterval(() => this._patchInteractiveCursors(), 500);
      }
    } else {
      document.removeEventListener('mousemove', this._bigCursorMoveBound);
      document.removeEventListener('mouseleave', this._bigCursorLeaveBound);
      if (this._bigCursorInterval) {
        clearInterval(this._bigCursorInterval);
        this._bigCursorInterval = null;
      }
      this._bigCursorEl?.remove();
      this._bigCursorEl = null;
      this._bigCursorStyleEl?.remove();
      this._bigCursorStyleEl = null;
      this._bigCursorMutObs?.disconnect();
      this._bigCursorMutObs = null;
      document.querySelectorAll('*').forEach(el => (el as HTMLElement).style?.removeProperty('cursor'));
    }
  }

  private _bigCursorMove(e: MouseEvent): void {
    if (!this._bigCursorEl) return;
    this._bigCursorEl.style.transform = `translate(${e.clientX - 8}px, ${e.clientY - 4}px)`;
    if (this._bigCursorEl.style.opacity !== '1') this._bigCursorEl.style.opacity = '1';
    const under = document.elementFromPoint(e.clientX, e.clientY);
    if (under) this._patchElementCursor(under as HTMLElement);
  }

  private _bigCursorLeave(): void {
    if (this._bigCursorEl) this._bigCursorEl.style.opacity = '0';
  }

  private _patchElementCursor(el: HTMLElement): void {
    el.style?.setProperty('cursor', BLANK_CURSOR, 'important');
  }

  private _patchAllDomCursors(): void {
    document.querySelectorAll('*').forEach(el => this._patchElementCursor(el as HTMLElement));
  }

  // Targets only interactive elements — cheap interval re-patch for elements
  // that slip past MutationObserver (async renders, Angular zone-driven updates)
  private _patchInteractiveCursors(): void {
    document.querySelectorAll("a, button, input, select, textarea, label, [role='button'], [role='link']")
      .forEach(el => this._patchElementCursor(el as HTMLElement));
  }

  private _save(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
  }

  // Iubenda injects the floating preferences button asynchronously via JS
  // and may set bottom via inline style, overriding CSS. Use MutationObserver
  // to detect the button and force it above the accessibility trigger via
  // setProperty with !important (only way to beat inline styles).
  private _repositionIubendaButton(): void {
    const stack = this.stackRef?.nativeElement;
    if (!stack) return;
    const btn = document.querySelector<HTMLElement>(
      '#iubenda-cs-preferences-link, .iubenda-cs-preferences-link, [id^="iubenda-"][class*="preferences"]'
    );
    if (!btn || btn.parentElement === stack) return;
    btn.style.setProperty('position', 'relative', 'important');
    btn.style.setProperty('bottom', 'auto', 'important');
    btn.style.setProperty('left', 'auto', 'important');
    btn.style.setProperty('right', 'auto', 'important');
    stack.insertBefore(btn, stack.firstChild);
    btn.querySelectorAll<HTMLElement>('label.iub-sr-only').forEach(lbl => {
      const span = document.createElement('span');
      span.className = lbl.className;
      span.innerHTML = lbl.innerHTML;
      lbl.parentNode?.replaceChild(span, lbl);
    });
  }

  private _watchIubendaButton(): void {
    this._repositionIubendaButton();
    this._iubendaMutObs = new MutationObserver(() => this._repositionIubendaButton());
    this._iubendaMutObs.observe(document.body, { childList: true, subtree: true });
  }
}
